<template>
  <div class="card-base hdl-root" id="hp-documents">
    <h4 class="card-title mb-2 text-ellipsis">{{ $t("homePage.documentsBox.title") }}</h4>
    <template v-if="loading">
      <InlineError v-if="hasError" class="state-empty" :message="$t('homePage.documentsBox.error')" />
      <div v-else-if="documents.length > 0" class="hdl-list flex flex-col gap-2 mt-2 mb-2">
        <div
          v-for="doc in documents"
          :key="doc.id"
          class="hdl-item flex justify-between items-center gap-3"
        >
          <div class="flex items-start gap-2 flex-1 min-w-0">
            <span class="material-icons icon-sm icon-gray flex-shrink-0 mt-1">description</span>
            <div class="min-w-0">
              <div class="flex items-center gap-1 flex-wrap text-navy font-bold">
                <span class="word-break-all">{{ doc.name }}</span>
              </div>
              <div class="text-xs text-gray mt-1">
                <span v-if="doc.category">{{ doc.category.name }} · </span>
                <span>{{ formatDate(doc.creation_date) }}</span>
              </div>
            </div>
          </div>
          <div class="flex items-center gap-3 flex-shrink-0">
            <ColoredLabels
              :label="doc.read ? $t('homePage.documentsBox.downloaded') : 'NEW'"
              :color="doc.read ? 'gray' : 'acqua'"
              text-color="white"
              :class="{ 'badge-downloaded': doc.read }"
            />
            <ActionButton
              custom-class="btn-download"
              icon="download"
              :title="$t('documents.actions.download')"
              :loading="downloadingId === doc.id"
              @click="$emit('download', doc)"
            >
              <span class="visually-hidden">{{ $t("documents.actions.download") }} {{ doc.name }}</span>
            </ActionButton>
          </div>
        </div>
      </div>
      <InlineError v-else class="state-empty" :message="$t('homePage.documentsBox.noDocuments')" />
    </template>
    <div v-else class="state-loading">
      <MyLoading :viewLoader="false" type="" />
    </div>
    <div class="hdl-footer border-t mt-2 pt-2">
      <router-link :to="'/' + $t('nav.documentsArchiveUrl')" class="text-navy text-sm">
        {{ $t("homePage.documentsBox.goToDocuments") }}
      </router-link>
    </div>
  </div>
</template>

<script setup>
import moment from "moment";
import MyLoading from "@/components/MyLoading.vue";
import ColoredLabels from "@/components/Primitives/Labels/ColoredLabels.vue";
import ActionButton from "@/components/Primitives/Buttons/ActionButton.vue";
import InlineError from "@/components/Primitives/Feedback/InlineError.vue";

/**
 * @component HomeDocumentList
 * @description Lista documenti recenti nella home page. Mostra loader, stato vuoto e lista con badge NEW.
 * La convenzione del progetto inverte il flag di caricamento: `loading === true` significa dati pronti.
 *
 * @prop {Array}       documents     - Documenti restituiti dall'API. Ogni elemento espone `id`, `name`,
 *                                     `read` (false = documento non letto → badge NEW), `category`, `creation_date`.
 * @prop {Boolean}     loading       - true = dati pronti e lista visibile; false = spinner attivo.
 * @prop {Number|null} downloadingId - ID numerico del documento in download; disabilita il pulsante corrispondente.
 *                                     null quando nessun download è in corso.
 *
 * @emits download - Payload: Object (documento da scaricare, passato direttamente al gestore del componente padre)
 *
 * @slot — nessuno slot esposto
 */
defineProps({
  documents: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  hasError: { type: Boolean, default: false },
  downloadingId: { type: Number, default: null },
});

defineEmits(["download"]);

function formatDate(date) {
  return moment(date, "YYYY-MM-DD").format("DD/MM/YYYY");
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";

.hdl-root {
  padding: 16px 20px 12px;
}

.hdl-item {
  border: 1px solid $grayFive;
  border-radius: 10px;
  padding: 10px 10px 10px 12px;
}

.hdl-footer a {
  color: $bluFour;
}
</style>